import { useCallback, useEffect, type DependencyList, type RefObject } from 'react';

const A4_ASPECT_HEIGHT_RATIO = 297 / 210;
const EXAMPLE_BODY_MIN_PX = 30;

function elementBlockHeight(el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const marginTop = parseFloat(style.marginTop) || 0;
  const marginBottom = parseFloat(style.marginBottom) || 0;
  return rect.height + marginTop + marginBottom;
}

/** A4 비율 페이지에서 예시 답안 본문 높이를 남는 영역에 맞춤 */
export function balanceEvaluationPlanWebDocumentA4Layout(page: HTMLElement): void {
  const pageWidth = page.clientWidth || page.offsetWidth;
  if (pageWidth <= 0) return;

  const targetPageHeight = pageWidth * A4_ASPECT_HEIGHT_RATIO;
  page.style.height = `${targetPageHeight}px`;
  page.style.minHeight = `${targetPageHeight}px`;

  const exampleBody = page.querySelector('.cp-evaluation-plan-web-doc-example-answer-body') as HTMLElement | null;
  const exampleSection = page.querySelector('.cp-evaluation-plan-web-doc-example-answer-section') as HTMLElement | null;
  if (!exampleBody || !exampleSection) return;

  const pageStyle = window.getComputedStyle(page);
  const pagePaddingTop = parseFloat(pageStyle.paddingTop) || 0;
  const pagePaddingBottom = parseFloat(pageStyle.paddingBottom) || 0;

  const blockSelectors = [
    '.cp-evaluation-plan-web-doc-title-row',
    '.cp-evaluation-plan-web-doc-school-year-line',
    '.cp-evaluation-plan-web-doc-meta-table',
    '.cp-evaluation-plan-web-doc-example-answer-header',
    '.cp-evaluation-plan-web-doc-rubric-table',
  ];

  let fixedHeight = pagePaddingTop + pagePaddingBottom;
  blockSelectors.forEach((selector) => {
    const el = page.querySelector(selector) as HTMLElement | null;
    if (el) fixedHeight += elementBlockHeight(el);
  });

  const bodyHeight = Math.max(EXAMPLE_BODY_MIN_PX, targetPageHeight - fixedHeight);

  exampleSection.style.flex = '1 1 auto';
  exampleSection.style.minHeight = '0';
  exampleBody.style.flex = '1 1 auto';
  exampleBody.style.minHeight = `${bodyHeight}px`;
  exampleBody.style.height = `${bodyHeight}px`;

  const fillTarget =
    (exampleBody.querySelector('.cp-evaluation-plan-web-doc-example-answer-readonly') as HTMLElement | null) ??
    (exampleBody.querySelector('.cp-evaluation-plan-web-doc-example-answer-input-shell') as HTMLElement | null) ??
    (exampleBody.querySelector('.cp-evaluation-plan-web-doc-cell-input-shell') as HTMLElement | null);

  if (fillTarget) {
    fillTarget.style.flex = '1 1 auto';
    fillTarget.style.minHeight = `${bodyHeight}px`;
    fillTarget.style.height = '100%';
  }

  page.style.setProperty('--cp-web-doc-example-body-min', `${bodyHeight}px`);
}

export function useEvaluationPlanWebDocumentA4Layout(
  pageRef: RefObject<HTMLElement | null>,
  deps: DependencyList = []
): void {
  const balance = useCallback(() => {
    const page = pageRef.current;
    if (!page) return;
    balanceEvaluationPlanWebDocumentA4Layout(page);
  }, [pageRef]);

  useEffect(() => {
    let rafId = 0;
    const scheduleBalance = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        balance();
      });
    };

    scheduleBalance();

    const page = pageRef.current;
    if (!page) {
      return () => {
        cancelAnimationFrame(rafId);
      };
    }

    const observer = new ResizeObserver(scheduleBalance);
    observer.observe(page);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 문서 구조 변경 시 재계산
  }, [balance, pageRef, ...deps]);
}
